import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "react-loader-spinner";
import { useIntercom } from "react-use-intercom";
import SideBar from "../../Sections/SideBar";
import Navbar from "../../Sections/NavBar";
import MobileNav from "../../Sections/MobileNav";

import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../../app-store/userSlice";
import InitiateTransfer from "./InitiateTransfer";
import ConfirmTransfer from "./ConfirmTransfer";
import Status from "./Status";
import httpClient from "../../../custom-hooks/httpClient";
import TwoFactorAuth from "../../2FA-OTP";

function TransferOtherWallet() {
  const userId = localStorage.getItem("id");

  const user = useSelector((state) => state.user.user);

  const dispatch = useDispatch();

  const { boot } = useIntercom();

  const [stage, setStage] = useState("initiate"); // initiate, confirm, status
  const [transferDetails, setTransferDetails] = useState({
    sender_wallet_number: "",
    transaction_pin: "",
    receiver_wallet_number: "",
    transfer_amount: "",
    wallet_name: "",
  });

  const [receiverName, setReceiverName] = useState("");
  const [transferStatus, setTransferStatus] = useState("");
  const [transferReference, setTransferReference] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [notificationChannel, setNotificationChannel] = useState("");
  const [recipient, setRecipient] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChangeTransferDetails = (e) => {
    const { name, value } = e.target;

    if (name === "sender_wallet_number") {
      const selectedWallet = user.wallets.find(
        (wallet) => wallet.account_no === value
      );
      if (selectedWallet) {
        setTransferDetails({
          ...transferDetails,
          sender_wallet_number: selectedWallet.account_no,
          wallet_name: selectedWallet.name,
        });
      }
    } else {
      setTransferDetails({
        ...transferDetails,
        [name]: value,
      });
    }
  };

  const handleInitiateTransfer = (e) => {
    e.preventDefault();
    setStage("confirm");
  };

  const onConfirm = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    // Set transfer amount to number
    const transfer = {
      ...transferDetails,
      transfer_amount: Number(transferDetails.transfer_amount),
    };

    try {
      const { status, data } = await httpClient.post(
        `/transfers/wallet-to-other-wallet/transfer-funds`,
        {
          ...transfer,
        },
      );

      if (status === 202) {
        setNotificationChannel(data.notificationChannel);
        setRecipient(data.recipient);
        setStage("authenticate");
      }
      if (status === 200) {
        setTransferReference(data.transfer.tx_ref);
        setTransferStatus("successful");
        setStage("status");
      }
    } catch (err) {
      setIsProcessing(false);
      toast.error(err.response.data.message);
      setTransferStatus("failed");
      setStage("status");
    }
  };

  const handleConfirmTransfer = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    // Set transfer amount to number
    const transfer = {
      ...transferDetails,
      transfer_amount: Number(transferDetails.transfer_amount),
    };
    const otp = document.getElementById("otp").value; // Get OTP from user input.

    try {
      const { status, data } = await httpClient.post(
        `/transfers/wallet-to-other-wallet/transfer-funds`,
        {
          ...transfer,
          otp,
        },
      );

      if (status === 200) {
        setTransferReference(data.transfer.tx_ref);
        setTransferStatus("successful");
        setStage("status");
      }
    } catch (err) {
      // Check for 401 with "invalid otp" message
      if (
        err.response?.status === 401 &&
        err.response?.data?.message === "Invalid otp"
      ) {
        setErrorMessage("Invalid OTP! Please, check and try again.");
        document.getElementById("otp").value = ""; // Reset OTP input field.
        setIsProcessing(false);
        setTimeout(() => {
          setErrorMessage("");
        }, 5000);
      } else {
        toast.error(err.response.data.message);
        setTransferStatus("failed");
        setStage("status");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveBeneficiary = async () => {
    try {
      const beneficiaryData = {
        name: receiverName,
        recipient_account_number: transferDetails.receiver_wallet_number,
        account_number: user.wallets[0].account_no,
        account_type: transferDetails.account_type || "",
        bank_code: transferDetails.bank_code || "",
        bank_image: transferDetails.bank_image || "",
      };

      // Check if the checkbox is checked to avoid calling the endpoint when unchecked.
      if (document.getElementById("saveBeneficiaryCheckbox").checked) {
        const { status } = await httpClient.post(
          `/beneficiaries/${transferDetails.receiver_wallet_number}`,
          beneficiaryData
        );

        if (status === 200) {
          toast.success("Beneficiary Saved Successfully!");
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data.message ||
          "Error Saving Beneficiary! Please, check that the beneficiary does not already exist."
      );
    }
  };

  useEffect(() => {
    httpClient
      .get(`/users/${userId}`)
      .then(({ status, data }) => {
        if (status === 200) {
          dispatch(setUser(data.user));
          boot({
            name: `${data.user.first_name} ${data.user.last_name}`,
          });
        }
      })
      .catch((err) => {
        console.log(err);
        // toast.error("Error getting user");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return (
      <div
        className="text-center align-items-center"
        style={{ marginTop: "25%" }}
      >
        <Loader type="Oval" color="#1c743a" />
      </div>
    );
  }

  return (
    <>
      <Navbar dashboard />
      <MobileNav />
      <section>
        <div className="container-fluid">
          <div className="row">
            <SideBar />
            <div className="col-12 col-lg-6 col-xl-8 offset-lg-3 offset-xl-2 py-7 py-lg-9 px-lg-7">
              {stage === "initiate" && (
                <InitiateTransfer
                  user={user}
                  handleInitiateTransfer={handleInitiateTransfer}
                  handleChangeTransferDetails={handleChangeTransferDetails}
                  transferDetails={transferDetails}
                  receiverName={receiverName}
                  setReceiverName={setReceiverName}
                  handleSaveBeneficiary={handleSaveBeneficiary}
                />
              )}
              {stage === "confirm" && (
                <ConfirmTransfer
                  handleChangeTransferDetails={handleChangeTransferDetails}
                  transferDetails={transferDetails}
                  receiverName={receiverName}
                  isProcessing={isProcessing}
                  onConfirm={onConfirm}
                />
              )}
              {stage === "authenticate" && (
                <TwoFactorAuth
                  handleConfirmTransfer={handleConfirmTransfer}
                  notificationChannel={notificationChannel}
                  recipient={recipient}
                  errorMessage={errorMessage}
                />
              )}
              {stage === "status" && (
                <Status
                  transferDetails={transferDetails}
                  receiverName={receiverName}
                  transferStatus={transferStatus}
                  transferReference={transferReference}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </>

    // <div className="dashboard pt-3">
    //   <div className="main">
    //     <SideBar user={user} />

    //     <div className="container-fluid pt-5">
    //       <div className="d-flex align-items-center justify-content-between mb-4">
    //         <h1 className="h1 mb-0 text-gray-800">
    //           Welcome, {user.first_name}
    //         </h1>
    //       </div>

    //       <div className="row">
    //         <div className="col-xl-12 col-md-12 mb-12">
    //           <div className="card border-left-primary shadow h-100 py-2">
    //             <div className="card-header py-3">
    //               <h6 className="m-0 font-weight-bold text-primary">
    //                 Transfer to another wallet
    //               </h6>
    //             </div>
    //             <div className="card-body">
    //               <form onSubmit={handleSubmit}>
    //                 <div className="form-group">
    //                   <label for="fromWallet">From Wallet</label>
    //                   <select
    //                     className="form-control"
    //                     id="fromWallet"
    //                     onChange={handleChange("fromWallet")}
    //                   >
    //                     <option disabled selected>
    //                       --select Account--
    //                     </option>
    //                     {user.wallets.map((wallet) => (
    //                       <>
    //                         <option value={parseInt(wallet.account_no)}>
    //                           {wallet.name} (&#8358;
    //                           {Number(wallet.balance)
    //                             .toFixed(2)
    //                             .toLocaleString()}
    //                           )
    //                         </option>
    //                       </>
    //                     ))}
    //                   </select>
    //                 </div>

    //                 <div className="input-group mb-3">
    //                   <div className="input-group-prepend">
    //                     <span className="input-group-text">To Wallet ID</span>
    //                   </div>

    //                   <input
    //                     id="toAccountNumber"
    //                     type="text"
    //                     className="form-control form-control-user"
    //                     placeholder=""
    //                     title="Numbers only"
    //                     maxLength="12"
    //                     required
    //                     onChange={handleChange("toAccountNumber")}
    //                   />
    //                   <div className="input-group-prepend">
    //                     <button
    //                       className="btn btn-primary btn-block"
    //                       type="button"
    //                       className="button"
    //                       onClick={handleVerifyWalletNo}
    //                     >
    //                       Validate
    //                     </button>
    //                   </div>
    //                 </div>
    //                 <div className="input-group mb-3">
    //                   <div className="input-group-prepend">
    //                     <span className="input-group-text">Wallet Owner:</span>
    //                   </div>
    //                   <input
    //                     className="form-control form-control-user"
    //                     type="text"
    //                     value={userInfo}
    //                     readOnly
    //                   />
    //                 </div>
    //                 <div className="input-group mb-3">
    //                   <div className="input-group-prepend">
    //                     <span className="input-group-text">Amount &#8358;</span>
    //                   </div>

    //                   <input
    //                     id="amount"
    //                     type="number"
    //                     className="form-control form-control-user"
    //                     placeholder="0.00"
    //                     title="Numbers only"
    //                     maxLength="10"
    //                     required
    //                     onChange={handleChange("amount")}
    //                   />
    //                 </div>
    //                 <button
    //                   type="submit"
    //                   className="btn btn-primary btn-lg btn-block mb-4"
    //                   disabled={!areFieldsSet}
    //                 >
    //                   Proceed
    //                 </button>
    //               </form>
    //             </div>
    //           </div>
    //         </div>
    //       </div>
    //     </div>
    //   </div>
    // </div>
  );
}

export default TransferOtherWallet;
